import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Booking, Call, Holiday, Item, Position, Quote, QuoteLineItem, RateCard, SharedAllocation } from '@gigdriver/domain';
import { buildLaborLines, quoteTotals } from '@gigdriver/domain';
import {
  getActiveQuote,
  watchOrgQuotes,
  getQuote as getQuoteApi,
  createQuote as createQuoteApi,
  updateQuote as updateQuoteApi,
  createChangeOrder as createChangeOrderApi,
  createInvoiceNinjaInvoice,
  refreshInvoiceNinjaInvoiceStatus,
} from '@gigdriver/client';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export const useQuotesStore = defineStore('quotes', () => {
  const currentQuote = ref<Quote | null>(null);
  // The quote this one supersedes, when currentQuote is a change order —
  // fetched alongside it so the UI can show a "previously approved" total.
  const parentQuote = ref<Quote | null>(null);
  // Org-wide view for the Gigs list: quoteId -> total, live. A gig's figure is
  // its activeQuoteId's total (there is no typed-in quoted amount any more).
  const orgQuoteTotals = ref<Record<string, number>>({});
  let stopOrgQuotes: (() => void) | null = null;
  function watchOrg(orgId: string) {
    stopOrgQuotes?.();
    stopOrgQuotes = watchOrgQuotes(
      orgId,
      quotes => {
        orgQuoteTotals.value = Object.fromEntries(quotes.map(q => [q.id, q.total]));
      },
      err => {
        error.value = err instanceof Error ? err.message : 'Failed to load quote totals';
      },
    );
  }
  function stopWatchingOrg() {
    stopOrgQuotes?.();
    stopOrgQuotes = null;
  }

  const isLoading = ref(false);
  const isSaving = ref(false);
  const isInvoicing = ref(false);
  const isRefreshingStatus = ref(false);
  const error = ref<string | null>(null);

  async function loadQuote(orgId: string, gigId: string) {
    try {
      isLoading.value = true;
      error.value = null;
      currentQuote.value = await getActiveQuote(orgId, gigId);
      parentQuote.value = currentQuote.value?.parentQuoteId
        ? await getQuoteApi(orgId, gigId, currentQuote.value.parentQuoteId)
        : null;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load quote';
    } finally {
      isLoading.value = false;
    }
  }

  // Creates an empty draft quote the first time the gig's line items are
  // touched. Every mutation below calls this first.
  async function ensureQuote(orgId: string, gigId: string, clientId: string): Promise<Quote> {
    if (currentQuote.value) return currentQuote.value;
    await createQuoteApi(orgId, gigId, {
      clientId,
      state: 'DRAFT',
      currency: 'USD',
      lineItems: [],
      ...quoteTotals([]),
    });
    await loadQuote(orgId, gigId);
    if (!currentQuote.value) throw new Error('Failed to create quote');
    return currentQuote.value;
  }

  async function persistLines(
    orgId: string,
    gigId: string,
    lineItems: QuoteLineItem[],
    taxRate?: number,
  ) {
    if (!currentQuote.value) return;
    try {
      isSaving.value = true;
      error.value = null;
      await updateQuoteApi(orgId, gigId, currentQuote.value.id, {
        lineItems,
        taxRate,
        ...quoteTotals(lineItems, taxRate),
      });
      await loadQuote(orgId, gigId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to save quote';
      throw err;
    } finally {
      isSaving.value = false;
    }
  }

  // Regenerates every 'labor' line from the gig's current positions/budgets
  // (max(maxBudget, actualOfferedCost) * margin — see buildLaborLines),
  // keeping any 'item' / 'manual' lines untouched.
  async function recomputeLaborFromBudget(
    orgId: string,
    gigId: string,
    clientId: string,
    positions: Position[],
    bookingsByPosition: Record<string, Booking[]>,
    calls: Call[],
    rateCards: RateCard[],
    marginPct: number,
    timeZone?: string,
    holidays?: Holiday[],
    shared?: SharedAllocation,
  ) {
    await ensureQuote(orgId, gigId, clientId);
    const laborLines = buildLaborLines(positions, bookingsByPosition, calls, rateCards, marginPct, timeZone, holidays, shared);
    const nonLabor = (currentQuote.value?.lineItems ?? []).filter(l => l.source !== 'labor');
    await persistLines(orgId, gigId, [...laborLines, ...nonLabor], currentQuote.value?.taxRate);
  }

  async function addItemLine(
    orgId: string,
    gigId: string,
    clientId: string,
    item: Item,
    quantity = 1,
  ) {
    await ensureQuote(orgId, gigId, clientId);
    const line: QuoteLineItem = {
      id: `item_${item.id}_${Date.now()}`,
      source: 'item',
      description: item.name,
      quantity,
      unitCost: item.unitCost,
      lineTotal: round2(item.unitCost * quantity),
      itemId: item.id,
      itemDescription: item.description,
      productKey: item.productKey,
    };
    await persistLines(
      orgId,
      gigId,
      [...(currentQuote.value?.lineItems ?? []), line],
      currentQuote.value?.taxRate,
    );
  }

  async function addManualLine(
    orgId: string,
    gigId: string,
    clientId: string,
    description: string,
    quantity: number,
    unitCost: number,
  ) {
    await ensureQuote(orgId, gigId, clientId);
    const line: QuoteLineItem = {
      id: `manual_${Date.now()}`,
      source: 'manual',
      description,
      quantity,
      unitCost,
      lineTotal: round2(unitCost * quantity),
    };
    await persistLines(
      orgId,
      gigId,
      [...(currentQuote.value?.lineItems ?? []), line],
      currentQuote.value?.taxRate,
    );
  }

  async function updateLine(
    orgId: string,
    gigId: string,
    lineId: string,
    updates: { quantity?: number; unitCost?: number },
  ) {
    if (!currentQuote.value) return;
    const lineItems = currentQuote.value.lineItems.map(line => {
      if (line.id !== lineId) return line;
      const quantity = updates.quantity ?? line.quantity;
      const unitCost = updates.unitCost ?? line.unitCost;
      return { ...line, quantity, unitCost, lineTotal: round2(quantity * unitCost) };
    });
    await persistLines(orgId, gigId, lineItems, currentQuote.value.taxRate);
  }

  async function removeLine(orgId: string, gigId: string, lineId: string) {
    if (!currentQuote.value) return;
    const lineItems = currentQuote.value.lineItems.filter(l => l.id !== lineId);
    await persistLines(orgId, gigId, lineItems, currentQuote.value.taxRate);
  }

  async function setTaxRate(orgId: string, gigId: string, taxRate: number | undefined) {
    if (!currentQuote.value) return;
    await persistLines(orgId, gigId, currentQuote.value.lineItems, taxRate);
  }

  async function setDeposit(orgId: string, gigId: string, depositAmount: number | undefined) {
    if (!currentQuote.value) return;
    try {
      isSaving.value = true;
      error.value = null;
      await updateQuoteApi(orgId, gigId, currentQuote.value.id, { depositAmount });
      await loadQuote(orgId, gigId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to save deposit amount';
      throw err;
    } finally {
      isSaving.value = false;
    }
  }

  async function sendQuote(orgId: string, gigId: string) {
    if (!currentQuote.value) return;
    try {
      error.value = null;
      await updateQuoteApi(orgId, gigId, currentQuote.value.id, {
        state: 'SENT',
        sentAt: new Date(),
      });
      await loadQuote(orgId, gigId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to send quote';
      throw err;
    }
  }

  async function markApproved(orgId: string, gigId: string, approvedBy: string) {
    if (!currentQuote.value) return;
    try {
      error.value = null;
      await updateQuoteApi(orgId, gigId, currentQuote.value.id, {
        state: 'APPROVED',
        approvedAt: new Date(),
        approvedBy,
      });
      await loadQuote(orgId, gigId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to mark the quote approved';
      throw err;
    }
  }

  async function markDeclined(orgId: string, gigId: string) {
    if (!currentQuote.value) return;
    try {
      error.value = null;
      await updateQuoteApi(orgId, gigId, currentQuote.value.id, { state: 'DECLINED' });
      await loadQuote(orgId, gigId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to decline the quote';
      throw err;
    }
  }

  // Clones the approved quote into a new editable DRAFT (a change order) and
  // makes it the gig's active quote.
  async function startChangeOrder(orgId: string, gigId: string) {
    if (!currentQuote.value) return;
    try {
      isSaving.value = true;
      error.value = null;
      await createChangeOrderApi(orgId, gigId, currentQuote.value.id);
      await loadQuote(orgId, gigId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create change order';
      throw err;
    } finally {
      isSaving.value = false;
    }
  }

  // Issues the invoice in Invoice Ninja from the approved quote; reloads the
  // quote afterward to pick up invoiceNinjaInvoiceId/invoicedAt.
  async function createInvoice(orgId: string, gigId: string) {
    if (!currentQuote.value) throw new Error('No quote to invoice');
    try {
      isInvoicing.value = true;
      error.value = null;
      const result = await createInvoiceNinjaInvoice({
        orgId,
        gigId,
        quoteId: currentQuote.value.id,
      });
      await loadQuote(orgId, gigId);
      return result;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create the invoice';
      throw err;
    } finally {
      isInvoicing.value = false;
    }
  }

  async function refreshInvoiceStatus(orgId: string, gigId: string) {
    try {
      isRefreshingStatus.value = true;
      error.value = null;
      return await refreshInvoiceNinjaInvoiceStatus({ orgId, gigId });
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to refresh payment status';
      throw err;
    } finally {
      isRefreshingStatus.value = false;
    }
  }

  return {
    orgQuoteTotals,
    watchOrg,
    stopWatchingOrg,
    currentQuote,
    parentQuote,
    isLoading,
    isSaving,
    isInvoicing,
    isRefreshingStatus,
    error,
    loadQuote,
    recomputeLaborFromBudget,
    addItemLine,
    addManualLine,
    updateLine,
    removeLine,
    setTaxRate,
    setDeposit,
    sendQuote,
    markApproved,
    markDeclined,
    startChangeOrder,
    createInvoice,
    refreshInvoiceStatus,
  };
});
